import { DEFAULT_GRID_COLUMNS } from '../PageConfigProvider/constants';
import type {
  ItemAlign,
  PositionedItem,
} from '../PageConfigProvider/types/item';
import type { ReportFlowPage, ReportFlowPageBlock } from './report-flow-types';

export type ReportFlowState = ReportFlowPage[];

export const blockItems = (block: ReportFlowPageBlock): PositionedItem[] =>
  block.kind === 'columns' ? block.items : [block.item];

export const pageItems = (pages: ReportFlowPage[]): PositionedItem[] =>
  pages.flatMap((page) => page.blocks.flatMap(blockItems));

const withItems = (items: PositionedItem[]): ReportFlowPageBlock[] => {
  const [first] = items;
  if (first === undefined) return [];
  if (items.length === 1) return [{ kind: 'item', item: first }];
  return [{ kind: 'columns', items }];
};

export const updateItem = (
  pages: ReportFlowPage[],
  itemId: string,
  update: (item: PositionedItem) => PositionedItem
): ReportFlowPage[] =>
  pages.map((page) => {
    let changed = false;
    const blocks = page.blocks.map((block): ReportFlowPageBlock => {
      if (!blockItems(block).some((item) => item.id === itemId)) return block;
      changed = true;
      return block.kind === 'item'
        ? { kind: 'item', item: update(block.item) }
        : {
            kind: 'columns',
            items: block.items.map((item) =>
              item.id === itemId ? update(item) : item
            ),
          };
    });
    return changed ? { ...page, blocks } : page;
  });

export const findItem = (
  pages: ReportFlowPage[],
  itemId: string
): PositionedItem | undefined =>
  pageItems(pages).find((item) => item.id === itemId);

export const removeItems = (
  pages: ReportFlowPage[],
  itemIds: ReadonlySet<string>
): ReportFlowPage[] => {
  let changed = false;
  const next = pages.map((page) => {
    if (
      !page.blocks.some((block) =>
        blockItems(block).some((item) => itemIds.has(item.id))
      )
    ) {
      return page;
    }
    changed = true;
    return {
      ...page,
      blocks: page.blocks.flatMap((block) => {
        const items = blockItems(block);
        const kept = items.filter((item) => !itemIds.has(item.id));
        return kept.length === items.length ? [block] : withItems(kept);
      }),
    };
  });
  return changed ? next : pages;
};

/** Placed items are updated in place, missing ones removed, new ones appended to the last page in grid order. */
export const replaceItems = (
  pages: ReportFlowPage[],
  items: PositionedItem[]
): ReportFlowPage[] => {
  const byId = new Map(items.map((item) => [item.id, item]));
  const placed = new Set<string>();
  const updated = pages.map((page) => {
    const blocks = page.blocks.flatMap((block) => {
      const current = blockItems(block);
      const next = current.flatMap((item) => {
        const replacement = byId.get(item.id);
        if (!replacement) return [];
        placed.add(item.id);
        return [replacement];
      });
      if (
        next.length === current.length &&
        next.every((item, i) => item === current[i])
      ) {
        return [block];
      }
      return withItems(next);
    });
    return blocks.length === page.blocks.length &&
      blocks.every((block, i) => block === page.blocks[i])
      ? page
      : { ...page, blocks };
  });

  const appended: ReportFlowPageBlock[] = items
    .filter((item) => !placed.has(item.id))
    .sort((a, b) => a.position.y - b.position.y || a.position.x - b.position.x)
    .map((item) => ({ kind: 'item', item }));
  if (appended.length === 0) {
    return updated.every((page, i) => page === pages[i]) ? pages : updated;
  }
  const last = updated[updated.length - 1];
  if (!last) return [{ id: 'page-1', blocks: appended }];
  return updated.map((page) =>
    page === last ? { ...page, blocks: [...page.blocks, ...appended] } : page
  );
};

const locateItem = (pages: ReportFlowPage[], itemId: string) => {
  for (const [pageIndex, page] of pages.entries()) {
    for (const [blockIndex, block] of page.blocks.entries()) {
      const columnIndex = blockItems(block).findIndex(
        (item) => item.id === itemId
      );
      if (columnIndex !== -1) return { pageIndex, blockIndex, columnIndex };
    }
  }
  return undefined;
};

const copyPages = (pages: ReportFlowPage[]): ReportFlowPage[] =>
  pages.map((page) => ({ ...page, blocks: [...page.blocks] }));

export type ReportFlowMoveBlockPayload = {
  pageId: string;
  blockIndex: number;
  direction: 'up' | 'down';
};

export const reportFlowMoveBlock = (
  state: ReportFlowState,
  { pageId, blockIndex, direction }: ReportFlowMoveBlockPayload
): ReportFlowState => {
  const pageIndex = state.findIndex((page) => page.id === pageId);
  const block = state[pageIndex]?.blocks[blockIndex];
  if (!block) return state;

  const pages = copyPages(state);
  const source = pages[pageIndex];
  if (!source) return state;
  source.blocks.splice(blockIndex, 1);

  if (direction === 'up') {
    if (blockIndex > 0) {
      source.blocks.splice(blockIndex - 1, 0, block);
    } else {
      const target = pages[pageIndex - 1];
      if (!target) return state;
      target.blocks.push(block);
    }
  } else {
    if (blockIndex < source.blocks.length) {
      source.blocks.splice(blockIndex + 1, 0, block);
    } else {
      const target = pages[pageIndex + 1];
      if (!target) return state;
      target.blocks.unshift(block);
    }
  }

  return pages;
};

export type ReportFlowDeleteItemPayload = { itemId: string };

export const reportFlowDeleteItem = (
  state: ReportFlowState,
  { itemId }: ReportFlowDeleteItemPayload
): ReportFlowState => removeItems(state, new Set([itemId]));

export type ReportFlowAddTextItemPayload = {
  itemId: string;
  pageId: string;
  index: number;
  textHtml?: string;
};

const nextFreePosition = (items: PositionedItem[]) => {
  const maxY = items.reduce(
    (max, item) => Math.max(max, item.position.y + item.position.height),
    0
  );
  return { x: 0, y: maxY + 1, width: 48, height: 8 };
};

const insertNewItemBlock = (
  state: ReportFlowState,
  pageId: string,
  index: number,
  newItem: PositionedItem
): ReportFlowState => {
  if (!state.some((page) => page.id === pageId)) return state;

  return state.map((page) => {
    if (page.id !== pageId) return page;
    const blocks = [...page.blocks];
    const at = Math.max(0, Math.min(index, blocks.length));
    blocks.splice(at, 0, { kind: 'item', item: newItem });
    return { ...page, blocks };
  });
};

export const reportFlowAddTextItem = (
  state: ReportFlowState,
  { itemId, pageId, index, textHtml }: ReportFlowAddTextItemPayload
): ReportFlowState =>
  insertNewItemBlock(state, pageId, index, {
    id: itemId,
    title: '',
    position: nextFreePosition(pageItems(state)),
    plot: { chartType: 'text', textHtml: textHtml ?? '' },
  });

export type ReportFlowAddChartItemPayload = {
  itemId: string;
  pageId: string;
  index: number;
};

export const reportFlowAddChartItem = (
  state: ReportFlowState,
  { itemId, pageId, index }: ReportFlowAddChartItemPayload
): ReportFlowState =>
  insertNewItemBlock(state, pageId, index, {
    id: itemId,
    title: '',
    position: nextFreePosition(pageItems(state)),
  });

export type ReportFlowMoveBlockToPayload = {
  fromPageId: string;
  fromIndex: number;
  toPageId: string;
  toIndex: number;
};

export const reportFlowMoveBlockTo = (
  state: ReportFlowState,
  { fromPageId, fromIndex, toPageId, toIndex }: ReportFlowMoveBlockToPayload
): ReportFlowState => {
  const pages = copyPages(state);
  const from = pages.find((page) => page.id === fromPageId);
  const to = pages.find((page) => page.id === toPageId);
  if (!from || !to) return state;

  const [block] = from.blocks.splice(fromIndex, 1);
  if (!block) return state;
  const at = Math.max(0, Math.min(toIndex, to.blocks.length));
  to.blocks.splice(at, 0, block);

  return pages;
};

export const MAX_REPORT_FLOW_COLUMNS = 3;

export type ReportFlowMoveItemPayload = {
  itemId: string;
  /** Join the columns of the block holding this item, beside it. */
  besideItemId?: string;
  side?: 'left' | 'right';
  /** Otherwise land as a standalone block at this position. */
  toPageId?: string;
  toIndex?: number;
};

/** Moves one item anywhere; targets are neighbour item ids because detaching shifts indexes. */
export const reportFlowMoveItem = (
  state: ReportFlowState,
  { itemId, besideItemId, side, toPageId, toIndex }: ReportFlowMoveItemPayload
): ReportFlowState => {
  if (besideItemId === itemId) return state;

  const origin = locateItem(state, itemId);
  if (!origin) return state;
  const moving = blockItems(
    (state[origin.pageIndex] as ReportFlowPage).blocks[
      origin.blockIndex
    ] as ReportFlowPageBlock
  )[origin.columnIndex] as PositionedItem;

  const pages = copyPages(removeItems(state, new Set([itemId])));

  if (besideItemId !== undefined) {
    const target = locateItem(pages, besideItemId);
    if (!target) return state;
    const page = pages[target.pageIndex];
    const block = page?.blocks[target.blockIndex];
    if (!page || !block) return state;

    const existing = blockItems(block);
    if (existing.length >= MAX_REPORT_FLOW_COLUMNS) return state;
    const at =
      existing.findIndex((item) => item.id === besideItemId) +
      (side === 'right' ? 1 : 0);
    const items = [...existing];
    items.splice(Math.max(0, at), 0, moving);
    page.blocks[target.blockIndex] = { kind: 'columns', items };

    return pages;
  }

  const destination =
    pages.find((page) => page.id === toPageId) ?? pages[origin.pageIndex];
  if (!destination) return state;
  const at = Math.max(
    0,
    Math.min(toIndex ?? destination.blocks.length, destination.blocks.length)
  );
  destination.blocks.splice(at, 0, { kind: 'item', item: moving });

  return pages;
};

export const REPORT_FLOW_MIN_ITEM_WIDTH_COLS = 8;
export const REPORT_FLOW_MIN_ITEM_HEIGHT_ROWS = 4;
export const REPORT_FLOW_MAX_ITEM_HEIGHT_ROWS = 32;

const clampCols = (width: number) =>
  Math.min(
    DEFAULT_GRID_COLUMNS,
    Math.max(REPORT_FLOW_MIN_ITEM_WIDTH_COLS, Math.round(width))
  );

const clampRows = (height: number) =>
  Math.min(
    REPORT_FLOW_MAX_ITEM_HEIGHT_ROWS,
    Math.max(REPORT_FLOW_MIN_ITEM_HEIGHT_ROWS, Math.round(height))
  );

export type ReportFlowResizeItemPayload = {
  itemId: string;
  /** Grid columns out of DEFAULT_GRID_COLUMNS (48 = full page width). */
  width?: number;
  /** Grid rows (one row = REPORT_FLOW_ROW_PX). */
  height?: number;
};

export const reportFlowResizeItem = (
  state: ReportFlowState,
  { itemId, width, height }: ReportFlowResizeItemPayload
): ReportFlowState => {
  if (width === undefined && height === undefined) return state;
  if (!locateItem(state, itemId)) return state;
  return updateItem(state, itemId, (item) => ({
    ...item,
    position: {
      ...item.position,
      ...(width !== undefined && { width: clampCols(width) }),
      ...(height !== undefined && { height: clampRows(height) }),
    },
  }));
};

export type ReportFlowSetItemAlignPayload = {
  itemId: string;
  align: ItemAlign;
};

export const reportFlowSetItemAlign = (
  state: ReportFlowState,
  { itemId, align }: ReportFlowSetItemAlignPayload
): ReportFlowState => {
  if (!locateItem(state, itemId)) return state;
  return updateItem(state, itemId, (item) => ({ ...item, align }));
};

export type ReportFlowAddColumnChartPayload = {
  itemId: string;
  pageId: string;
  blockIndex: number;
  columnIndex: number;
};

export const reportFlowAddColumnChart = (
  state: ReportFlowState,
  { itemId, pageId, blockIndex, columnIndex }: ReportFlowAddColumnChartPayload
): ReportFlowState => {
  const page = state.find((p) => p.id === pageId);
  const block = page?.blocks[blockIndex];
  if (!page || !block || block.kind !== 'columns') return state;
  if (block.items.length >= MAX_REPORT_FLOW_COLUMNS) return state;

  const siblings = block.items;
  const meanWidth = Math.round(
    siblings.reduce((sum, item) => sum + item.position.width, 0) /
      Math.max(siblings.length, 1)
  );
  const rowHeight = siblings.reduce(
    (max, item) => Math.max(max, item.position.height),
    REPORT_FLOW_MIN_ITEM_HEIGHT_ROWS
  );
  const { y } = nextFreePosition(pageItems(state));
  const newItem: PositionedItem = {
    id: itemId,
    title: '',
    position: { x: 0, y, width: meanWidth, height: rowHeight },
  };

  const items = [...block.items];
  const at = Math.max(0, Math.min(columnIndex, items.length));
  items.splice(at, 0, newItem);

  return state.map((p) => {
    if (p.id !== pageId) return p;
    const blocks = [...p.blocks];
    blocks[blockIndex] = { kind: 'columns', items };
    return { ...p, blocks };
  });
};

export type ReportFlowAddPagePayload = {
  pageId: string;
  afterPageId?: string;
  /** Insert as the first page instead of after a page (or at the end). */
  atStart?: boolean;
};

export const reportFlowAddPage = (
  state: ReportFlowState,
  { pageId, afterPageId, atStart }: ReportFlowAddPagePayload
): ReportFlowState => {
  const pages = [...state];
  const anchorIndex = () => {
    if (atStart) return -1;
    const found = pages.findIndex((page) => page.id === afterPageId);
    return found === -1 ? pages.length - 1 : found;
  };
  pages.splice(anchorIndex() + 1, 0, { id: pageId, blocks: [] });
  return pages;
};

export type ReportFlowDeletePagePayload = { pageId: string };

export const reportFlowDeletePage = (
  state: ReportFlowState,
  { pageId }: ReportFlowDeletePagePayload
): ReportFlowState => {
  if (state.length <= 1) return state;
  if (!state.some((page) => page.id === pageId)) return state;
  return state.filter((page) => page.id !== pageId);
};

export type ReportFlowAction =
  | { type: 'REPORT_FLOW_MOVE_BLOCK'; payload: ReportFlowMoveBlockPayload }
  | { type: 'REPORT_FLOW_MOVE_BLOCK_TO'; payload: ReportFlowMoveBlockToPayload }
  | { type: 'REPORT_FLOW_MOVE_ITEM'; payload: ReportFlowMoveItemPayload }
  | {
      type: 'REPORT_FLOW_ADD_CHART_ITEM';
      payload: ReportFlowAddChartItemPayload;
    }
  | { type: 'REPORT_FLOW_DELETE_ITEM'; payload: ReportFlowDeleteItemPayload }
  | { type: 'REPORT_FLOW_ADD_TEXT_ITEM'; payload: ReportFlowAddTextItemPayload }
  | { type: 'REPORT_FLOW_ADD_PAGE'; payload: ReportFlowAddPagePayload }
  | { type: 'REPORT_FLOW_DELETE_PAGE'; payload: ReportFlowDeletePagePayload }
  | { type: 'REPORT_FLOW_RESIZE_ITEM'; payload: ReportFlowResizeItemPayload }
  | {
      type: 'REPORT_FLOW_SET_ITEM_ALIGN';
      payload: ReportFlowSetItemAlignPayload;
    }
  | {
      type: 'REPORT_FLOW_ADD_COLUMN_CHART';
      payload: ReportFlowAddColumnChartPayload;
    };

export const applyReportFlowAction = (
  state: ReportFlowState,
  action: ReportFlowAction
): ReportFlowState => {
  switch (action.type) {
    case 'REPORT_FLOW_MOVE_BLOCK':
      return reportFlowMoveBlock(state, action.payload);
    case 'REPORT_FLOW_MOVE_BLOCK_TO':
      return reportFlowMoveBlockTo(state, action.payload);
    case 'REPORT_FLOW_MOVE_ITEM':
      return reportFlowMoveItem(state, action.payload);
    case 'REPORT_FLOW_ADD_CHART_ITEM':
      return reportFlowAddChartItem(state, action.payload);
    case 'REPORT_FLOW_DELETE_ITEM':
      return reportFlowDeleteItem(state, action.payload);
    case 'REPORT_FLOW_ADD_TEXT_ITEM':
      return reportFlowAddTextItem(state, action.payload);
    case 'REPORT_FLOW_ADD_PAGE':
      return reportFlowAddPage(state, action.payload);
    case 'REPORT_FLOW_DELETE_PAGE':
      return reportFlowDeletePage(state, action.payload);
    case 'REPORT_FLOW_RESIZE_ITEM':
      return reportFlowResizeItem(state, action.payload);
    case 'REPORT_FLOW_SET_ITEM_ALIGN':
      return reportFlowSetItemAlign(state, action.payload);
    case 'REPORT_FLOW_ADD_COLUMN_CHART':
      return reportFlowAddColumnChart(state, action.payload);
  }
};
